import {
  Dashboard as DashboardIcon,
  Extension as WidgetIcon,
  Search as SEOIcon,
  SmartToy as BotIcon,
  PhoneAndroid as MobileIcon,
  Speed as PerformanceIcon,
  CheckCircle as CheckIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  ArrowForward as ArrowIcon,
  TrendingUp as TrendingIcon,
  Security as SecurityIcon,
  Star as StarIcon,
  Rocket as RocketIcon,
  Visibility as VisibilityIcon,
  Code as CodeIcon,
  Cloud as CloudIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
  Avatar
} from '@mui/material';
import { useEffect, useState } from 'react';

const PlatformFeaturesPremium = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [visibleCards, setVisibleCards] = useState(new Set());
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Premium feature data with enhanced content
  const features = [
    {
      id: 1,
      icon: <DashboardIcon />,
      title: 'AI-Powered Content Management',
      subtitle: 'Next-Gen Admin Dashboard',
      description: 'Enterprise seviyesinde içerik yönetimi. GPT-4 entegrasyonu ile otomatik içerik üretimi ve optimizasyon.',
      items: [
        'GPT-4 AI content generation',
        'Advanced role-based permissions',
        'Real-time collaboration tools',
        'Multi-language content management',
        'Automated workflow systems'
      ],
      color: '#4f46e5',
      gradient: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
      lightColor: '#e0e7ff',
      badge: 'AI-Powered',
      rating: 4.9,
      users: '10K+'
    },
    {
      id: 2,
      icon: <WidgetIcon />,
      title: 'Advanced Drag & Drop Builder',
      subtitle: 'Visual Page Constructor',
      description: 'React-Grid-Layout ile sınırsız tasarım özgürlüğü. Component library ve custom widget desteği.',
      items: [
        'Visual drag & drop interface',
        'Custom component library',
        'Real-time preview system',
        'Responsive design tools',
        'Template marketplace'
      ],
      color: '#059669',
      gradient: 'linear-gradient(135deg, #059669 0%, #0891b2 100%)',
      lightColor: '#d1fae5',
      badge: 'Visual Builder',
      rating: 4.8,
      users: '8K+'
    },
    {
      id: 3,
      icon: <SEOIcon />,
      title: 'Advanced SEO & Analytics',
      subtitle: 'Search Engine Optimization',
      description: 'Google Core Web Vitals optimizasyonu. Advanced SEO tools ve Google News entegrasyonu.',
      items: [
        'Core Web Vitals optimization',
        'Google News integration',
        'Advanced schema markup',
        'SEO performance tracking',
        'Automated sitemap generation'
      ],
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
      lightColor: '#fef3c7',
      badge: 'SEO Pro',
      rating: 4.7,
      users: '12K+'
    },
    {
      id: 4,
      icon: <BotIcon />,
      title: 'Intelligent News Automation',
      subtitle: 'AI Content Aggregation',
      description: 'Machine learning ile akıllı haber toplama. Otomatik kategorilendirme ve çoklu kaynak entegrasyonu.',
      items: [
        'Multi-source news aggregation',
        'AI-powered categorization',
        'Duplicate content detection',
        'Auto-publishing workflows',
        'Content quality scoring'
      ],
      color: '#7c3aed',
      gradient: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
      lightColor: '#ede9fe',
      badge: 'Automation',
      rating: 4.6,
      users: '6K+'
    },
    {
      id: 5,
      icon: <MobileIcon />,
      title: 'Cross-Platform Mobile Suite',
      subtitle: 'Native Mobile Experience',
      description: 'React Native ile iOS/Android native apps. Progressive Web App desteği ve offline functionality.',
      items: [
        'iOS & Android native apps',
        'Progressive Web App (PWA)',
        'Offline content access',
        'Push notification system',
        'Mobile-first responsive design'
      ],
      color: '#dc2626',
      gradient: 'linear-gradient(135deg, #dc2626 0%, #ea580c 100%)',
      lightColor: '#fee2e2',
      badge: 'Mobile Suite',
      rating: 4.8,
      users: '15K+'
    },
    {
      id: 6,
      icon: <PerformanceIcon />,
      title: 'Ultra-High Performance',
      subtitle: 'Next.js 14 & Edge Computing',
      description: 'Edge computing ile global performans. Server-side rendering ve advanced caching strategies.',
      items: [
        'Next.js 14 with App Router',
        'Edge computing deployment',
        'Advanced caching strategies',
        'Image optimization & CDN',
        'Core Web Vitals: 95+ score'
      ],
      color: '#0891b2',
      gradient: 'linear-gradient(135deg, #0891b2 0%, #059669 100%)',
      lightColor: '#cffafe',
      badge: 'Performance',
      rating: 4.9,
      users: '20K+'
    }
  ];

  // Enhanced stats with more impressive metrics
  const stats = [
    { 
      icon: <TrendingIcon />, 
      value: '99.99%', 
      label: 'Enterprise Uptime', 
      color: '#10b981',
      description: 'SLA guaranteed uptime'
    },
    { 
      icon: <PerformanceIcon />, 
      value: '<150ms', 
      label: 'Global Load Time', 
      color: '#f59e0b',
      description: 'Edge-optimized delivery'
    },
    { 
      icon: <StarIcon />, 
      value: '50K+', 
      label: 'Active Projects', 
      color: '#6366f1',
      description: 'Worldwide deployments'
    },
    { 
      icon: <SecurityIcon />, 
      value: 'SOC 2', 
      label: 'Security Certified', 
      color: '#ef4444',
      description: 'Enterprise compliance'
    }
  ];

  // Mouse tracking for interactive effects
  const handleMouseMove = (e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  // Enhanced intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setTimeout(() => {
              setVisibleCards(prev => new Set([...prev, index]));
            }, index * 100);
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const cards = document.querySelectorAll('[data-index]');
    cards.forEach(card => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  // Add mouse move listener
  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <Box
      sx={{
        py: { xs: 12, md: 20 },
        background: `
          linear-gradient(180deg, #f8fafc 0%, #ffffff 30%, #f1f5f9 70%, #e2e8f0 100%),
          radial-gradient(circle at 25% 25%, rgba(79, 70, 229, 0.02) 0%, transparent 50%),
          radial-gradient(circle at 75% 75%, rgba(5, 150, 105, 0.02) 0%, transparent 50%)
        `,
        color: '#0f172a',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            conic-gradient(from 0deg at 20% 20%, rgba(79, 70, 229, 0.01), transparent, rgba(79, 70, 229, 0.01)),
            conic-gradient(from 90deg at 80% 80%, rgba(5, 150, 105, 0.01), transparent, rgba(5, 150, 105, 0.01))
          `,
          pointerEvents: 'none'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: 400,
          height: 400,
          background: `radial-gradient(circle, rgba(79, 70, 229, 0.03) 0%, transparent 70%)`,
          borderRadius: '50%',
          left: mousePosition.x - 200,
          top: mousePosition.y - 200,
          pointerEvents: 'none',
          transition: 'all 0.3s ease',
          opacity: 0.4
        }
      }}
    >
      <Container maxWidth="xl">
        {/* Premium Header */}
        <Box sx={{ textAlign: 'center', mb: 14, position: 'relative' }}>
          <Stack 
            direction="row" 
            spacing={2} 
            sx={{ justifyContent: 'center', mb: 4 }}
          >
            <Chip
              icon={<RocketIcon />}
              label="Enterprise Platform"
              sx={{
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                color: 'white',
                fontWeight: 700,
                fontSize: '1rem',
                px: 3,
                py: 1.5,
                '& .MuiChip-icon': {
                  color: 'white'
                }
              }}
            />
            <Chip
              icon={<StarIcon />}
              label="AI-Powered"
              sx={{
                background: 'linear-gradient(135deg, #059669 0%, #0891b2 100%)',
                color: 'white',
                fontWeight: 700,
                fontSize: '1rem',
                px: 3,
                py: 1.5,
                '& .MuiChip-icon': {
                  color: 'white'
                }
              }}
            />
          </Stack>

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '3rem', md: '5.5rem' },
              fontWeight: 900,
              mb: 5,
              background: `
                linear-gradient(135deg, #0f172a 0%, #1e293b 20%, #4f46e5 60%, #7c3aed 100%)
              `,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              lineHeight: 0.9,
              position: 'relative',
              textShadow: '0 0 60px rgba(79, 70, 229, 0.3)',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -16,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 150,
                height: 8,
                background: 'linear-gradient(90deg, #4f46e5, #7c3aed, #059669)',
                borderRadius: 4,
                animation: 'shimmer 2s ease-in-out infinite'
              }
            }}
          >
            Platform Excellence
          </Typography>

          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.5rem', md: '2rem' },
              color: '#475569',
              maxWidth: 900,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 400,
              mb: 3
            }}
          >
            Enterprise-grade dijital yayıncılık platformu ile geleceğin medya deneyimini bugün yaşayın
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: '#64748b',
              fontSize: '1.2rem',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <StarIcon key={star} sx={{ fontSize: '1.2rem', color: '#fbbf24' }} />
                ))}
              </Box>
              <Typography variant="inherit" sx={{ fontWeight: 600 }}>
                4.9/5
              </Typography>
            </Box>
            • Trusted by 50,000+ enterprises worldwide
          </Typography>
        </Box>

        {/* Enhanced Stats Section */}
        <Grid container spacing={4} sx={{ mb: 12 }}>
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 5,
                  textAlign: 'center',
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  borderRadius: 4,
                  position: 'relative',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  transition: 'all 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${stat.color}, ${stat.color}80)`,
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.3s ease'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: `0 20px 40px ${stat.color}20, 0 10px 20px rgba(0, 0, 0, 0.1)`,
                    borderColor: stat.color,
                    background: 'rgba(255, 255, 255, 0.95)',
                    '&::before': {
                      transform: 'scaleX(1)'
                    }
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 3,
                    borderRadius: 3,
                    background: `${stat.color}15`,
                    color: stat.color,
                    mb: 3,
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Box sx={{ fontSize: '2rem' }}>
                    {stat.icon}
                  </Box>
                </Box>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 900, 
                    color: stat.color, 
                    mb: 1,
                    fontSize: '2.5rem'
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#1e293b', 
                    fontWeight: 700,
                    mb: 1
                  }}
                >
                  {stat.label}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#64748b',
                    fontSize: '0.9rem'
                  }}
                >
                  {stat.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Premium Features Grid */}
        <Grid container spacing={5} sx={{ mb: 16 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={feature.id}>
              <Card
                data-index={index}
                elevation={0}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                sx={{
                  p: 6,
                  height: '100%',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  borderRadius: 5,
                  position: 'relative',
                  cursor: 'pointer',
                  opacity: visibleCards.has(index) ? 1 : 0,
                  transform: visibleCards.has(index) 
                    ? 'translateY(0) scale(1) rotateX(0deg)' 
                    : 'translateY(40px) scale(0.95) rotateX(5deg)',
                  transition: 'all 0.8s cubic-bezier(0.23, 1, 0.32, 1)',
                  transformStyle: 'preserve-3d',
                  perspective: 1000,
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 5,
                    background: feature.gradient,
                    transform: hoveredCard === index ? 'scaleX(1)' : 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.5s cubic-bezier(0.23, 1, 0.32, 1)'
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -80,
                    right: -80,
                    width: 160,
                    height: 160,
                    background: `conic-gradient(from 0deg, ${feature.color}08, transparent, ${feature.color}08)`,
                    borderRadius: '50%',
                    opacity: hoveredCard === index ? 1 : 0,
                    transition: 'all 0.6s ease',
                    animation: hoveredCard === index ? 'spin 6s linear infinite' : 'none',
                    pointerEvents: 'none'
                  },
                  '&:hover': {
                    transform: 'translateY(-12px) scale(1.03) rotateX(-2deg)',
                    boxShadow: `
                      0 25px 50px ${feature.color}15,
                      0 15px 30px rgba(0, 0, 0, 0.1),
                      inset 0 1px 0 rgba(255, 255, 255, 0.6)
                    `,
                    borderColor: feature.color,
                    background: 'rgba(255, 255, 255, 1)',
                    '& .feature-icon': {
                      transform: 'scale(1.1) rotate(5deg)',
                      background: feature.gradient,
                      boxShadow: `0 10px 30px ${feature.color}30`
                    }
                  }
                }}
              >
                {/* Premium Badge & Rating */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 1
                  }}
                >
                  <Chip
                    label={feature.badge}
                    size="small"
                    sx={{
                      background: feature.lightColor,
                      color: feature.color,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      border: `1px solid ${feature.color}30`
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <StarIcon sx={{ fontSize: '1rem', color: '#fbbf24' }} />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: '#64748b' }}
                    >
                      {feature.rating}
                    </Typography>
                  </Box>
                </Box>

                {/* Enhanced Feature Icon */}
                <Box
                  className="feature-icon"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 4,
                    width: 85,
                    height: 85,
                    background: feature.lightColor,
                    borderRadius: 4,
                    color: feature.color,
                    position: 'relative',
                    transition: 'all 0.6s cubic-bezier(0.23, 1, 0.32, 1)',
                    transformStyle: 'preserve-3d'
                  }}
                >
                  <Box sx={{ fontSize: '2.2rem', zIndex: 2, position: 'relative' }}>
                    {feature.icon}
                  </Box>
                </Box>

                {/* Premium Content */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    mb: 1.5,
                    color: '#0f172a',
                    fontSize: '1.5rem',
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                    lineHeight: 1.2
                  }}
                >
                  {feature.title}
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    background: feature.gradient,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: '1.1rem'
                  }}
                >
                  {feature.subtitle}
                </Typography>

                {/* User Count */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 3
                  }}
                >
                  <Avatar
                    sx={{
                      width: 20,
                      height: 20,
                      background: feature.gradient,
                      fontSize: '0.7rem'
                    }}
                  >
                    {feature.users.charAt(0)}
                  </Avatar>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      fontWeight: 600,
                      fontSize: '0.85rem'
                    }}
                  >
                    {feature.users} active users
                  </Typography>
                </Box>

                <Typography
                  variant="body1"
                  sx={{
                    color: '#475569',
                    lineHeight: 1.7,
                    mb: 4,
                    fontSize: '1rem'
                  }}
                >
                  {feature.description}
                </Typography>

                <Divider sx={{ my: 3, borderColor: `${feature.color}20` }} />

                {/* Enhanced Feature List */}
                <List dense sx={{ p: 0, mb: 4 }}>
                  {feature.items.map((item, idx) => (
                    <ListItem 
                      key={idx} 
                      sx={{ 
                        px: 0, 
                        py: 1,
                        opacity: visibleCards.has(index) ? 1 : 0,
                        transform: visibleCards.has(index) ? 'translateX(0)' : 'translateX(-20px)',
                        transition: `all 0.6s cubic-bezier(0.23, 1, 0.32, 1) ${0.8 + idx * 0.1}s`
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon 
                          sx={{ 
                            fontSize: 18, 
                            color: feature.color,
                            background: feature.lightColor,
                            borderRadius: '50%',
                            p: 0.3
                          }} 
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontWeight: 500,
                          color: '#334155',
                          fontSize: '0.95rem'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>

                {/* Premium Action Button */}
                <Button
                  variant="text"
                  endIcon={<ArrowIcon />}
                  sx={{
                    color: '#64748b',
                    fontWeight: 700,
                    px: 0,
                    py: 1.5,
                    fontSize: '1rem',
                    textTransform: 'none',
                    transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: 0,
                      height: 2,
                      background: feature.gradient,
                      transition: 'width 0.3s ease'
                    },
                    '& .MuiButton-endIcon': {
                      transition: 'transform 0.4s cubic-bezier(0.23, 1, 0.32, 1)'
                    },
                    '&:hover': {
                      background: 'transparent',
                      color: feature.color,
                      '&::before': {
                        width: '100%'
                      },
                      '& .MuiButton-endIcon': {
                        transform: 'translateX(8px) scale(1.2)'
                      }
                    }
                  }}
                >
                  Explore Features
                </Button>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Premium Call to Action */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 8, md: 12 },
            textAlign: 'center',
            background: `
              linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%),
              conic-gradient(from 0deg, rgba(79, 70, 229, 0.08), rgba(5, 150, 105, 0.08), rgba(245, 158, 11, 0.08), rgba(79, 70, 229, 0.08))
            `,
            backdropFilter: 'blur(20px)',
            borderRadius: 8,
            border: '1px solid rgba(226, 232, 240, 0.8)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 30% 30%, rgba(79, 70, 229, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 70% 70%, rgba(5, 150, 105, 0.05) 0%, transparent 50%),
                linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)
              `,
              pointerEvents: 'none',
              animation: 'shimmer 4s ease-in-out infinite'
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.8rem', md: '4rem' },
                fontWeight: 900,
                mb: 4,
                background: `
                  linear-gradient(135deg, #0f172a 0%, #4f46e5 50%, #059669 100%)
                `,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                position: 'relative',
                textShadow: '0 0 40px rgba(79, 70, 229, 0.3)'
              }}
            >
              Ready to Transform?
            </Typography>

            <Typography
              variant="h5"
              sx={{
                color: '#475569',
                mb: 6,
                maxWidth: 800,
                mx: 'auto',
                fontSize: '1.4rem',
                fontWeight: 400,
                lineHeight: 1.6
              }}
            >
              Join 50,000+ enterprises already using our platform to revolutionize their digital publishing experience
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={4}
              sx={{ justifyContent: 'center', mb: 8 }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<RocketIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #059669 100%)',
                  color: 'white',
                  fontWeight: 800,
                  px: 12,
                  py: 4,
                  fontSize: '1.3rem',
                  borderRadius: 4,
                  textTransform: 'none',
                  boxShadow: '0 12px 40px rgba(79, 70, 229, 0.4)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: -100,
                    width: 100,
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                    animation: 'shimmer 2s ease-in-out infinite'
                  },
                  '&:hover': {
                    background: 'linear-gradient(135deg, #3730a3 0%, #6b21a8 50%, #047857 100%)',
                    transform: 'translateY(-3px) scale(1.05)',
                    boxShadow: '0 20px 60px rgba(79, 70, 229, 0.5)'
                  },
                  transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)'
                }}
              >
                Start Enterprise Trial
              </Button>

              <Button
                variant="outlined"
                size="large"
                startIcon={<VisibilityIcon />}
                sx={{
                  borderColor: '#cbd5e1',
                  color: '#475569',
                  fontWeight: 700,
                  px: 12,
                  py: 4,
                  fontSize: '1.3rem',
                  borderRadius: 4,
                  textTransform: 'none',
                  borderWidth: 2,
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    borderColor: '#4f46e5',
                    color: '#4f46e5',
                    background: 'rgba(255, 255, 255, 0.9)',
                    transform: 'translateY(-3px) scale(1.05)',
                    boxShadow: '0 15px 40px rgba(79, 70, 229, 0.2)'
                  },
                  transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)'
                }}
              >
                Schedule Demo
              </Button>
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '1rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <CheckIcon sx={{ color: '#10b981', fontSize: '1.2rem' }} />
                30-day free trial
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '1rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <CheckIcon sx={{ color: '#10b981', fontSize: '1.2rem' }} />
                No credit card required
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '1rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <CheckIcon sx={{ color: '#10b981', fontSize: '1.2rem' }} />
                24/7 enterprise support
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Enhanced CSS Animations */}
      <style jsx global>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-15deg); }
          100% { transform: translateX(200%) skewX(-15deg); }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(3deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.02); }
        }

        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(79, 70, 229, 0.3); }
          50% { box-shadow: 0 0 40px rgba(79, 70, 229, 0.6); }
        }
      `}</style>
    </Box>
  );
};

export default PlatformFeaturesPremium;
